import styled from '@emotion/styled';
import { type BarChart, type ChartData, type LineChart, type MultipleBarChart, type MultipleLineChart, type PieChart, type QuantFileTableData } from '@trinity/chat';

type ChartDataTableProps = {
  data?: unknown;
  chartData?: ChartData;
  chartType?: string;
};

export function ChartDataTable({ data, chartData, chartType }: ChartDataTableProps) {
  const convertChartDataToTable = () => {
    if (!chartData?.chart?.chart || !chartData?.table) {
      return [];
    }

    const { chart: chartConfig, table } = chartData;
    let tableData: Record<string, string | number>[];

    switch (chartType) {
      case 'pie':
        tableData = convertPieChartData(chartConfig.chart as PieChart, table);
        break;
      case 'bar':
        tableData = convertBarChartData(chartConfig.chart as BarChart, table);
        break;
      case 'stacked-bar':
        tableData = convertBarChartData(chartConfig.chart as BarChart, table);
        break;
      case 'multiple-clustered-bar':
        tableData = convertMultipleBarChartData(chartConfig.chart as MultipleBarChart, table);
        break;
      case 'multiple-stacked-bar':
        tableData = convertMultipleBarChartData(chartConfig.chart as MultipleBarChart, table);
        break;
      case 'line':
        tableData = convertLineChartData(chartConfig.chart as LineChart, table);
        break;
      case 'multiple-line':
        tableData = convertMultipleLineChartData(chartConfig.chart as MultipleLineChart, table);
        break;
      case 'none':
        return [];
      default:
        return [];
    }
    return tableData;
  };

  const convertPieChartData = (chart: PieChart, table: QuantFileTableData) => {
    const tableData: Record<string, string | number>[] = [];
    const { columnNames, rowNames, records } = table;

    chart.data.forEach((item, itemIndex) => {
      const row: Record<string, string | number> = {
        Label: item.label,
        Value: item.value,
        ID: item.id,
      };

      // Try to find additional data from the table records
      const matchingRowIndex = rowNames.findIndex(rowName => {
        const rowString = Array.isArray(rowName) ? rowName.join(' ') : String(rowName);
        return rowString.includes(item.label) || rowString === item.label;
      });

      if (matchingRowIndex !== -1 && records[matchingRowIndex]) {
        const recordData = records[matchingRowIndex];
        columnNames.forEach((col, colIndex) => {
          const columnLabel = Array.isArray(col) ? col.join(' ') : String(col);
          if (recordData[colIndex] !== undefined &&
            !columnLabel.toLowerCase().includes('column') &&
            columnLabel.trim() !== '' &&
            !['Label', 'Value', 'ID'].includes(columnLabel)) {
            row[columnLabel] = recordData[colIndex];
          }
        });
      }

      // Calculate percentage if not already present
      if (!('Percentage' in row)) {
        const total = chart.data.reduce((sum, dataItem) => sum + dataItem.value, 0);
        if (total > 0) {
          row['Percentage'] = `${((item.value / total) * 100).toFixed(1)}%`;
        }
      }
      tableData.push(row);
    });
    return tableData;
  };

  const convertBarChartData = (chart: BarChart, table: QuantFileTableData) => {
    const tableData: Record<string, string | number>[] = [];

    if (Array.isArray(chart.data)) {
      chart.data.forEach(item => {
        const row: Record<string, string | number> = {};
        row[chart.indexBy] = item.x;
        row['Value'] = item.y;
        tableData.push(row);
      });
    }

    return tableData;
  };

  const convertMultipleBarChartData = (chart: MultipleBarChart, table: QuantFileTableData) => {
    const tableData: Record<string, string | number>[] = [];
    const { columnNames, rowNames, records: _records } = table;

    // Get category names from rowNames (exclude utility rows)
    const categoryNames = rowNames
      .filter(row => Array.isArray(row) && row.length > 0 && !row[0].includes('Column'))
      .map(row => row[0]);

    categoryNames.forEach((categoryName, categoryIndex) => {
      const tableRow: Record<string, string | number> = {
        [chart.indexBy]: categoryName,
      };

      // Add data for each series
      chart.data.forEach(seriesData => {
        if (!seriesData || typeof seriesData !== 'object' || !('index' in seriesData) || !('data' in seriesData)) return;
        const seriesName = String(seriesData.index);
        const seriesValues = seriesData.data;

        if (!Array.isArray(seriesValues) || seriesValues[categoryIndex] === undefined) return;
        const columnLabel = resolveColumnLabel(columnNames, seriesName, categoryIndex);
        tableRow[columnLabel] = resolveCellValue(seriesValues[categoryIndex]);
      });
      tableData.push(tableRow);
    });
    return tableData;
  };

  function resolveColumnLabel(columnNames: QuantFileTableData['columnNames'], seriesName: string, _categoryIndex: number): string {
    const seriesColumnIndex = columnNames.findIndex(col => {
      const colString = Array.isArray(col) ? col.join(' ') : String(col);
      return colString.includes(seriesName);
    });
    return seriesColumnIndex !== -1 ? columnNames[seriesColumnIndex].join(' ') : seriesName;
  }

  function resolveCellValue(cellValue: unknown): string | number {
    if (typeof cellValue === 'number') return cellValue;
    if (typeof cellValue === 'object' && cellValue !== null) {
      const valueObj = cellValue as Record<string, unknown>;
      if ('value' in valueObj && typeof valueObj.value === 'number') return valueObj.value;
      if ('y' in valueObj && typeof valueObj.y === 'number') return valueObj.y;
      return JSON.stringify(cellValue);
    }
    return String(cellValue);
  }

  const convertLineChartData = (chart: LineChart, table: QuantFileTableData) => {
    const tableData: Record<string, string | number>[] = [];

    let dataPoints: unknown[] = [];

    if (chart.data && typeof chart.data === 'object') {
      if ('data' in chart.data && Array.isArray(chart.data.data)) {
        dataPoints = chart.data.data;
      } else if (Array.isArray(chart.data)) {
        dataPoints = chart.data;
      }
    }
    if (dataPoints.length > 0) {
      dataPoints.forEach((point, pointIndex) => {
        tableData.push(convertLineDataPoint(point, pointIndex));
      });
    }
    return tableData;
  };

  function convertLineDataPoint(point: unknown, pointIndex: number): Record<string, string | number> {
    if (!point || typeof point !== 'object') {
      return { 'Label': `Point ${pointIndex + 1}`, 'Value': String(point || 0) };
    }
    const pointObj = point as Record<string, unknown>;
    const label = 'x' in pointObj && typeof pointObj.x === 'string' ? pointObj.x : `Point ${pointIndex + 1}`;
    const value = 'y' in pointObj && typeof pointObj.y === 'number' ? pointObj.y : String(pointObj.y || 0);
    return { 'Label': label, 'Value': value };
  }

  const convertMultipleLineChartData = (chart: MultipleLineChart, table: QuantFileTableData) => {
    const tableData: Record<string, string | number>[] = [];
    const { columnNames, rowNames, records } = table;

    const categoryNames = rowNames
      .filter(row => Array.isArray(row) && row.length > 0 && !row[0].includes('Column'))
      .map(row => row[0]);

    categoryNames.forEach((categoryName, categoryIndex) => {
      const tableRow: Record<string, string | number> = {
        Category: categoryName,
      };

      chart.data.forEach((line, lineIndex) => {
        if (Array.isArray(line) && line[categoryIndex]) {
          const point = line[categoryIndex] as unknown;
          const seriesLabel = `Series ${lineIndex + 1}`;

          let value: string | number = 'N/A';
          if (point && typeof point === 'object') {
            const pointObj = point as Record<string, unknown>;
            if ('y' in pointObj && typeof pointObj.y === 'number') {
              value = pointObj.y;
            } else if ('value' in pointObj && typeof pointObj.value === 'number') {
              value = pointObj.value;
            }
          } else if (typeof point === 'number') {
            value = point;
          }

          tableRow[seriesLabel] = value;
        }
      });

      const recordData = records[categoryIndex];
      if (recordData && recordData.length > 0) {
        columnNames.forEach((col, colIndex) => {
          const columnLabel = Array.isArray(col) ? col.join(' ') : String(col);
          if (recordData[colIndex] !== undefined && !columnLabel.toLowerCase().includes('column')) {
            const existingSeriesKey = Object.keys(tableRow).find(key =>
              key.startsWith('Series') && columnLabel.includes(key.replace('Series ', ''))
            );
            if (!existingSeriesKey) {
              tableRow[columnLabel] = recordData[colIndex];
            }
          }
        });
      }
      tableData.push(tableRow);
    });
    return tableData;
  };

  const tableData = convertChartDataToTable();

  if (!tableData || tableData.length === 0) {
    return <div>No table data available</div>;
  }

  const headers = Object.keys(tableData[0]);

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            {headers.map((header, index) => (
              <TableHeaderCell key={index}>{header}</TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableData.map((row, index) => (
            <TableRow key={index}>
              {headers.map((header, cellIndex) => (
                <TableCell key={cellIndex}>{String(row[header] || '')}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const TableContainer = styled.div(({ theme }) => ({
  width: '100%',
  maxHeight: '400px',
  overflowY: 'auto',
  border: `1px solid ${theme?.palette?.gray?.light1 || '#e0e0e0'}`,
  borderRadius: '6px',
  backgroundColor: theme?.palette?.white?.main || '#fff',
  marginBottom: '12px',
}));

const Table = styled.table({
  width: '100%',
  borderCollapse: 'collapse',
});

const TableHeader = styled.thead(({ theme }) => ({
  backgroundColor: theme?.palette?.gray?.light2 || '#f5f5f5',
  position: 'sticky',
  top: 0,
  zIndex: 1,
}));

const TableBody = styled.tbody({});

const TableRow = styled.tr(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: theme?.palette?.gray?.light3 || '#fafafa',
  },
  '&:hover': {
    backgroundColor: theme?.palette?.gray?.light2 || '#f0f0f0',
  },
}));

const TableHeaderCell = styled.th(({ theme }) => ({
  padding: '12px 16px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '14px',
  color: theme?.palette?.gray?.dark1 || '#333',
  borderBottom: `2px solid ${theme?.palette?.gray?.light1 || '#e0e0e0'}`,
}));

const TableCell = styled.td(({ theme }) => ({
  padding: '12px 16px',
  fontSize: '14px',
  color: theme?.palette?.gray?.dark1 || '#333',
  borderBottom: `1px solid ${theme?.palette?.gray?.light2 || '#f0f0f0'}`,
}));
